import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AFO from "../../../../assets/images/AFO.png";
import { isActive, visibleMenu } from "../../../layout/menu";
import { can, SESSION_EVENT } from "../../../../utils/roles";
import { authSession } from "../../../../services/auth";
import "../../../layout/shell.css";

const toggleSidebar = () => document.body.classList.toggle("sidebar-main");

// Below this width the sidebar is a drawer opened by `sidebar-main`.
const DRAWER_MAX_WIDTH = 1299;
const closeDrawer = () => {
  if (window.innerWidth <= DRAWER_MAX_WIDTH)
    document.body.classList.remove("sidebar-main");
};

const NavLink = ({ item, active }) => {
  const { t } = useTranslation();
  return (
    <Link
      to={item.path}
      className="app-nav-link"
      aria-current={active ? "page" : undefined}
    >
      <i className={item.icon} aria-hidden="true"></i>
      <span className="app-nav-label">{t(item.label)}</span>
    </Link>
  );
};

const NavGroup = ({ item, pathname, open, onToggle }) => {
  const { t } = useTranslation();
  const active = isActive(item, pathname);
  return (
    <li className="app-nav-group" data-active={active ? "" : undefined}>
      <button
        type="button"
        className="app-nav-link"
        aria-expanded={open}
        onClick={onToggle}
      >
        <i className={item.icon} aria-hidden="true"></i>
        <span className="app-nav-label">{t(item.label)}</span>
        <i
          className="ri-arrow-down-s-line app-nav-chevron"
          aria-hidden="true"
        ></i>
      </button>
      {open && (
        <ul className="app-subnav">
          {item.children.map((child) => (
            <li key={child.path}>
              <Link
                to={child.path}
                className="app-subnav-link"
                aria-current={pathname === child.path ? "page" : undefined}
              >
                {t(child.label)}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const SidebarStyle = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Bumped when the cached session changes, to rebuild the menu.
  const [sessionVersion, setSessionVersion] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const menu = useMemo(() => visibleMenu(can), [sessionVersion]);

  // Permissions can change while the admin is logged in: reload them from the
  // API once per visit. An invalid or deactivated account goes back to login.
  useEffect(() => {
    const onSession = () => setSessionVersion((version) => version + 1);
    window.addEventListener(SESSION_EVENT, onSession);
    if (localStorage.getItem("token")) {
      authSession().then((valid) => {
        if (!valid) window.location.assign("/");
      });
    }
    return () => window.removeEventListener(SESSION_EVENT, onSession);
  }, []);
  const [openGroup, setOpenGroup] = useState(null);

  // Close the mobile drawer once a page is chosen.
  useEffect(closeDrawer, [pathname]);

  // Open the section of the current page.
  useEffect(() => {
    const current = menu.find(
      (item) => item.children && isActive(item, pathname)
    );
    if (current) setOpenGroup(current.label);
  }, [menu, pathname]);

  return (
    <>
      <div
        className="app-drawer-backdrop"
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <div className="iq-sidebar app-sidebar">
        <div className="app-sidebar-brand">
          <Link to="/dashboard" className="app-sidebar-logo">
            <img src={AFO} alt="" />
            <span className="app-nav-label">AFO MEDIA</span>
          </Link>
          <button
            type="button"
            className="app-icon-btn app-sidebar-collapse"
            onClick={toggleSidebar}
            aria-label={t("Toggle menu")}
          >
            <i className="ri-indent-decrease" aria-hidden="true"></i>
          </button>
        </div>

        <nav className="app-nav" aria-label={t("Main navigation")}>
          <ul>
            {menu.map((item) =>
              item.children ? (
                <NavGroup
                  key={item.label}
                  item={item}
                  pathname={pathname}
                  open={openGroup === item.label}
                  onToggle={() =>
                    setOpenGroup(openGroup === item.label ? null : item.label)
                  }
                />
              ) : (
                <li key={item.label}>
                  <NavLink item={item} active={isActive(item, pathname)} />
                </li>
              )
            )}
          </ul>
          <a
            href="https://afomedia.com/"
            className="app-nav-link app-nav-external"
            target="_blank"
            rel="noreferrer"
          >
            <i className="ri-share-box-line" aria-hidden="true"></i>
            <span className="app-nav-label">{t("Visit site")}</span>
          </a>
        </nav>
      </div>
    </>
  );
};

export default SidebarStyle;
